import React, { useState, useEffect } from 'react';
import { appointmentService, Appointment, Professional } from '../services/appointmentService';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, AlertTriangle, X } from 'lucide-react';
import toast from 'react-hot-toast';

const AdminCalendar: React.FC = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [bookings, setBookings] = useState<Appointment[]>([]);
  const [professionals, setProfessionals] = useState<Professional[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedProfessional, setSelectedProfessional] = useState<number | null>(null);
  const [viewRange, setViewRange] = useState<'1day' | '3days' | 'week'>('3days');
  
  // Selection & Modals
  const [selectedBooking, setSelectedBooking] = useState<Appointment | null>(null);
  const [showPopup, setShowPopup] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Forms
  const [editFormData, setEditFormData] = useState({
    date: '',
    start_time: '',
    end_time: '',
    customer_name: '',
    customer_phone: '',
    customer_email: '',
  });

  const [createFormData, setCreateFormData] = useState({
    date: '',
    start_time: '',
    end_time: '',
    customer_name: '',
    customer_phone: '',
    customer_email: '',
    professional: '',
    services: [1] as number[], // Default service
  });

  // Force Booking State
  const [showForceConfirm, setShowForceConfirm] = useState(false);
  const [conflictMessage, setConflictMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    loadProfessionals();
  }, []);

  useEffect(() => {
    loadBookings();
  }, [currentDate, viewRange]);

  const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const loadProfessionals = async () => {
    try {
      const profsData = await appointmentService.getAllProfessionals();
      setProfessionals(Array.isArray(profsData) ? profsData : []);
    } catch (err) {
      console.error('Error cargando profesionales', err);
    }
  };

  const loadBookings = async () => {
    setLoading(true);
    try {
      let count = 1;
      if (viewRange === '3days') count = 3;
      if (viewRange === 'week') count = 7;
      
      const startDate = new Date(currentDate);
      const endDate = new Date(currentDate);
      endDate.setDate(endDate.getDate() + count - 1);
      
      const startStr = formatDate(startDate);
      const endStr = formatDate(endDate);

      const bookingsData = await appointmentService.getCalendarBookings(startStr, endStr);
      setBookings(Array.isArray(bookingsData) ? bookingsData : []);
    } catch (err) {
      console.error('Error cargando datos del calendario', err);
    } finally {
      setLoading(false);
    }
  };

  const loadData = async () => {
    await Promise.all([loadProfessionals(), loadBookings()]);
  };

  const getDisplayDates = () => {
    const days: Date[] = [];
    let count = 1;
    if (viewRange === '3days') count = 3;
    if (viewRange === 'week') count = 7;

    for (let i = 0; i < count; i++) {
      const day = new Date(currentDate);
      day.setDate(currentDate.getDate() + i);
      days.push(day);
    }
    return days;
  };

  const formatTime = (time: string) => {
    return time.substring(0, 5); // HH:MM
  };

  const handlePrevious = () => {
    const newDate = new Date(currentDate);
    const daysToJump = viewRange === 'week' ? 7 : viewRange === '3days' ? 3 : 1;
    newDate.setDate(currentDate.getDate() - daysToJump);
    setCurrentDate(newDate);
  };

  const handleNext = () => {
    const newDate = new Date(currentDate);
    const daysToJump = viewRange === 'week' ? 7 : viewRange === '3days' ? 3 : 1;
    newDate.setDate(currentDate.getDate() + daysToJump);
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const displayDates = getDisplayDates();
  const isOneDayView = viewRange === '1day';
  
  const visibleProfessionals = selectedProfessional 
    ? professionals.filter(p => p.id === selectedProfessional)
    : professionals;

  const gridColumnsCount = isOneDayView ? visibleProfessionals.length : displayDates.length;
  const gridTemplateColumns = `60px repeat(${gridColumnsCount}, 1fr)`;

  const workHours = Array.from({ length: 14 }, (_, i) => i + 8); // 8 a 21

  const getBookingPosition = (startTime: string, endTime: string) => {
    const startHour = parseInt(startTime.split(':')[0]);
    const startMin = parseInt(startTime.split(':')[1]);
    const endHour = parseInt(endTime.split(':')[0]);
    const endMin = parseInt(endTime.split(':')[1]);

    const top = ((startHour - 8) * 60 + startMin) / 60 * 80; 
    const height = ((endHour * 60 + endMin) - (startHour * 60 + startMin)) / 60 * 80;

    return { top, height };
  };

  const handleBookingClick = (booking: Appointment, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering slot click
    setSelectedBooking(booking);
    setShowPopup(true);
  };

  const handleSlotClick = (colIdx: number, hour: number) => {
    let dateStr = '';
    let professionalId = '';

    if (isOneDayView) {
        // Column is Professional
        const prof = visibleProfessionals[colIdx];
        dateStr = formatDate(currentDate);
        professionalId = String(prof.id);
    } else {
        // Column is Date
        const date = displayDates[colIdx];
        dateStr = formatDate(date);
        professionalId = selectedProfessional ? String(selectedProfessional) : '';
    }

    const startTime = `${String(hour).padStart(2, '0')}:00`;
    const endTime = `${String(hour + 1).padStart(2, '0')}:00`;

    setCreateFormData({
        date: dateStr,
        start_time: startTime,
        end_time: endTime,
        customer_name: '',
        customer_phone: '',
        customer_email: '',
        professional: professionalId,
        services: [1]
    });
    setShowCreateModal(true);
    setShowForceConfirm(false);
    setConflictMessage('');
  };

  const handleCreateSubmit = async (force: boolean = false) => {
    if (isSubmitting) return; // Prevent double submission
    setIsSubmitting(true);
    try {
        const payload = {
            ...createFormData,
            professional: Number(createFormData.professional),
            force_booking: force
        };

        await appointmentService.createBooking(payload);
        toast.success('Cita creada exitosamente');
        setShowCreateModal(false);
        loadBookings();
    } catch (error: any) {
        console.error("Error creating appointment:", error);
        if (error.response && error.response.data) {
            const data = error.response.data;
            if (data.code === 'OVERLAP_DETECTED' || (typeof data === 'object' && JSON.stringify(data).includes('OVERLAP_DETECTED'))) {
               const clientName = data.conflicting_client || "otro cliente";
               setConflictMessage(`Este horario está ocupado por ${clientName}. ¿Deseas agendar de todos modos?`);
               setShowForceConfirm(true);
               setIsSubmitting(false);
               return;
            }
            if (data.detail && String(data.detail).includes('ocupado')) {
                 setConflictMessage(`Este horario está ocupado. ¿Deseas agendar de todos modos?`);
                 setShowForceConfirm(true);
                 setIsSubmitting(false);
                 return;
            }
            toast.error(data.detail || "Error al crear la cita");
        } else {
            toast.error("Error de conexión");
        }
    } finally {
        setIsSubmitting(false);
    }
  };

  const closePopup = () => {
    setShowPopup(false);
    setSelectedBooking(null);
  };

  const handleEditClick = () => {
    if (!selectedBooking) return;
    setEditFormData({
      date: selectedBooking.date,
      start_time: selectedBooking.start_time,
      end_time: selectedBooking.end_time,
      customer_name: selectedBooking.customer_name || '',
      customer_phone: selectedBooking.customer_phone || '',
      customer_email: selectedBooking.customer_email || '',
    });
    setShowPopup(false);
    setShowEditModal(true);
  };

  const handleDeleteClick = async () => {
    if (!selectedBooking) return;
    if (window.confirm(`¿Está seguro de que desea eliminar la cita de ${selectedBooking.customer_name || 'este cliente'}?`)) {
      try {
        await appointmentService.deleteBooking(selectedBooking.id);
        toast.success('Cita eliminada exitosamente');
        setShowPopup(false);
        setSelectedBooking(null);
        loadBookings();
      } catch (error) {
        console.error('Error al eliminar la cita:', error);
        toast.error('Error al eliminar la cita');
      }
    }
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedBooking) return;
    try {
      await appointmentService.updateBooking(selectedBooking.id, editFormData);
      toast.success('Cita actualizada exitosamente');
      setShowEditModal(false);
      setSelectedBooking(null);
      loadData();
    } catch (error) {
      console.error('Error al actualizar la cita:', error);
      toast.error('Error al actualizar la cita');
    }
  };

  const getColumnBookings = (colIndex: number) => {
    if (isOneDayView) {
      const prof = visibleProfessionals[colIndex];
      if (!prof) return [];
      const dateStr = formatDate(currentDate);
      return bookings.filter(b => b.professional === prof.id && b.date === dateStr);
    } else {
      const date = displayDates[colIndex];
      if (!date) return [];
      const dateStr = formatDate(date);
      let filtered = bookings.filter(b => b.date === dateStr);
      if (selectedProfessional) {
        filtered = filtered.filter(b => b.professional === selectedProfessional);
      }
      return filtered;
    }
  };

  return (
    <div>
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Calendario de Reservas</h2>
            <div className="flex items-center gap-4">
               <input 
                 type="date" 
                 value={formatDate(currentDate)}
                 onChange={(e) => setCurrentDate(new Date(e.target.value + 'T12:00:00'))}
                 className="px-3 py-1 border border-gray-300 rounded-lg text-sm"
               />
               <div className="flex bg-gray-100 rounded-lg p-1">
                 <button onClick={() => setViewRange('1day')} className={`px-3 py-1 text-sm rounded-md transition-colors ${viewRange === '1day' ? 'bg-white shadow text-gray-900' : 'text-gray-600 hover:text-gray-900'}`}>1 Día</button>
                 <button onClick={() => setViewRange('3days')} className={`px-3 py-1 text-sm rounded-md transition-colors ${viewRange === '3days' ? 'bg-white shadow text-gray-900' : 'text-gray-600 hover:text-gray-900'}`}>3 Días</button>
                 <button onClick={() => setViewRange('week')} className={`px-3 py-1 text-sm rounded-md transition-colors ${viewRange === 'week' ? 'bg-white shadow text-gray-900' : 'text-gray-600 hover:text-gray-900'}`}>Semana</button>
               </div>
            </div>
          </div>
          
          <div className="flex flex-wrap items-center gap-3">
            {!isOneDayView && (
              <select
                value={selectedProfessional || ''}
                onChange={e => setSelectedProfessional(e.target.value ? Number(e.target.value) : null)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
              >
                <option value="">Todos los profesionales</option>
                {professionals.map(prof => (
                  <option key={prof.id} value={prof.id}>{prof.first_name} {prof.last_name}</option>
                ))}
              </select>
            )}
            <div className="flex items-center gap-2">
              <button onClick={handlePrevious} className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50"><ChevronLeft className="w-4 h-4" /></button>
              <button onClick={goToToday} className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm">Hoy</button>
              <button onClick={handleNext} className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50"><ChevronRight className="w-4 h-4" /></button>
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <div className="w-12 h-12 border-4 border-gray-200 border-t-gray-900 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando calendario...</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="grid border-b border-gray-200" style={{ gridTemplateColumns: gridTemplateColumns }}>
            <div className="p-2 bg-gray-50 border-r border-gray-200 flex items-center justify-center">
              <CalendarIcon className="w-4 h-4 text-gray-400" />
            </div>
            {isOneDayView ? (
              visibleProfessionals.map((prof) => (
                <div key={prof.id} className="p-4 text-center border-r border-gray-200 last:border-r-0 bg-gray-50">
                  <div className="font-semibold text-gray-900">{prof.first_name} {prof.last_name}</div>
                  <div className="text-xs text-gray-500">{prof.specialty || 'Profesional'}</div>
                </div>
              ))
            ) : (
              displayDates.map((date, idx) => {
                const isToday = formatDate(date) === formatDate(new Date());
                return (
                  <div key={idx} className={`p-4 text-center border-r border-gray-200 last:border-r-0 ${isToday ? 'bg-blue-50' : 'bg-gray-50'}`}>
                    <div className="text-xs text-gray-500 uppercase">{date.toLocaleDateString('es-ES', { weekday: 'short' })}</div>
                    <div className={`text-lg font-semibold mt-1 ${isToday ? 'text-blue-700' : 'text-gray-900'}`}>{date.getDate()}</div>
                    <div className="text-xs text-gray-500">{date.toLocaleDateString('es-ES', { month: 'short' })}</div>
                  </div>
                );
              })
            )}
          </div>

          <div className="overflow-x-auto">
            <div className="grid" style={{ gridTemplateColumns: gridTemplateColumns, minWidth: '700px' }}>
              <div className="border-r border-gray-200">
                {workHours.map(hour => (
                  <div key={hour} className="h-20 border-b border-gray-200 px-1 py-2 text-xs text-gray-600 font-semibold text-center">
                    {String(hour).padStart(2, '0')}:00
                  </div>
                ))}
              </div>

              {Array.from({ length: gridColumnsCount }).map((_, colIdx) => {
                const colBookings = getColumnBookings(colIdx);
                const bookingsWithColumns = colBookings.map((booking, idx) => {
                  const overlapping = colBookings.filter((other, otherIdx) => {
                    if (idx === otherIdx) return false;
                    return booking.start_time < other.end_time && other.start_time < booking.end_time;
                  });
                  let column = 0;
                  const overlappingBefore = colBookings.filter((other, otherIdx) => {
                    if (otherIdx >= idx) return false;
                    return booking.start_time < other.end_time && other.start_time < booking.end_time;
                  });
                  column = overlappingBefore.length;
                  const totalColumns = overlapping.length + 1;
                  return { booking, column, totalColumns };
                });

                return (
                  <div key={colIdx} className="border-r border-gray-200 last:border-r-0 relative">
                    {workHours.map(hour => (
                      <div
                        key={hour}
                        onClick={() => handleSlotClick(colIdx, hour)}
                        className="h-20 border-b border-gray-200 hover:bg-gray-50 cursor-pointer transition-colors"
                      ></div>
                    ))}
                    <div className="absolute top-0 left-0 right-0 bottom-0 pointer-events-none">
                      {bookingsWithColumns.map(({ booking, column, totalColumns }, idx) => {
                        const { top, height } = getBookingPosition(booking.start_time, booking.end_time);
                        const widthPercent = 100 / totalColumns;
                        const leftPercent = (column * widthPercent);
                        
                        // Determinar tamaño del bloque para adaptar contenido
                        const isVerySmall = height < 30; // menos de ~22 min
                        const isSmall = height < 50; // menos de ~37 min
                        const isMedium = height < 80; // menos de 1 hora
                        
                        // Altura mínima visual para que siempre se vea algo
                        const displayHeight = Math.max(height, 24);
                        
                        // Colores por columna para diferenciar reservas superpuestas
                        const columnColors = [
                          { bg: 'bg-blue-50', border: 'border-blue-500', hover: 'hover:bg-blue-100' },
                          { bg: 'bg-amber-50', border: 'border-amber-500', hover: 'hover:bg-amber-100' },
                          { bg: 'bg-emerald-50', border: 'border-emerald-500', hover: 'hover:bg-emerald-100' },
                          { bg: 'bg-purple-50', border: 'border-purple-500', hover: 'hover:bg-purple-100' },
                          { bg: 'bg-rose-50', border: 'border-rose-500', hover: 'hover:bg-rose-100' },
                        ];
                        const colorScheme = columnColors[column % columnColors.length];
                        
                        // Colores basados en status (override si hay status especial)
                        const statusColors: Record<string, { bg: string; border: string; text: string }> = {
                          'CONFIRMED': { bg: colorScheme.bg, border: colorScheme.border, text: 'text-gray-900' },
                          'PENDING': { bg: 'bg-orange-50', border: 'border-orange-400', text: 'text-gray-900' },
                          'COMPLETED': { bg: 'bg-gray-100', border: 'border-green-500', text: 'text-gray-600' },
                          'CANCELLED': { bg: 'bg-gray-100', border: 'border-gray-300', text: 'text-gray-400' },
                        };
                        const colors = statusColors[booking.status] || { bg: colorScheme.bg, border: colorScheme.border, text: 'text-gray-900' };
                        
                        // Tooltip con toda la info
                        const tooltipText = `${formatTime(booking.start_time)} - ${formatTime(booking.end_time)}\n${booking.customer_name || 'Sin nombre'}\n${booking.professional_detail ? `${booking.professional_detail.first_name} ${booking.professional_detail.last_name || ''}` : ''}\n${booking.appointment_services?.map((s: any) => s.name).join(', ') || ''}`;
                        
                        return (
                          <div
                            key={idx}
                            onClick={(e) => handleBookingClick(booking, e)}
                            title={tooltipText}
                            className={`absolute rounded-md overflow-hidden shadow-sm hover:shadow-lg hover:scale-[1.02] hover:z-50 transition-all border-l-4 pointer-events-auto cursor-pointer
                                ${colors.bg} ${colors.border} ${colors.text}
                                ${booking.status === 'CANCELLED' ? 'opacity-60' : ''}`}
                            style={{ 
                                top: `${top}px`, 
                                height: `${displayHeight}px`, 
                                left: `${leftPercent + 0.5}%`, 
                                width: `${widthPercent - 1.5}%`,
                                minWidth: '60px',
                                zIndex: 10 + column
                            }}
                          >
                            {/* Contenido adaptativo según tamaño */}
                            <div className="h-full px-1.5 py-0.5 flex flex-col justify-center">
                              {isVerySmall ? (
                                // Bloque muy pequeño: solo nombre truncado
                                <div className="font-semibold text-[10px] truncate leading-tight">
                                  {booking.customer_name || 'Sin nombre'}
                                </div>
                              ) : isSmall ? (
                                // Bloque pequeño: hora + nombre
                                <>
                                  <div className="text-[9px] font-medium opacity-70 truncate">
                                    {formatTime(booking.start_time)}-{formatTime(booking.end_time)}
                                  </div>
                                  <div className="font-semibold text-[11px] truncate leading-tight">
                                    {booking.customer_name || 'Sin nombre'}
                                  </div>
                                </>
                              ) : isMedium ? (
                                // Bloque mediano: hora, nombre, profesional
                                <>
                                  <div className="text-[9px] font-medium opacity-70 truncate border-b border-black/5 pb-0.5 mb-0.5">
                                    {formatTime(booking.start_time)} - {formatTime(booking.end_time)}
                                  </div>
                                  <div className="font-bold text-xs truncate leading-tight">
                                    {booking.customer_name || 'Sin nombre'}
                                  </div>
                                  <div className="text-[10px] truncate opacity-80 flex items-center gap-1">
                                    <span className="w-1 h-1 rounded-full bg-current opacity-50"></span>
                                    {booking.professional_detail ? `${booking.professional_detail.first_name}` : ''}
                                  </div>
                                </>
                              ) : (
                                // Bloque grande: todo el detalle
                                <>
                                  <div className="flex items-center justify-between text-[10px] font-semibold opacity-80 border-b border-black/10 pb-0.5 mb-1">
                                    <span>{formatTime(booking.start_time)} - {formatTime(booking.end_time)}</span>
                                  </div>
                                  
                                  <div className="flex flex-col gap-0.5 min-h-0 flex-1">
                                    <div className="font-bold text-xs truncate leading-snug">
                                      {booking.customer_name || 'Sin nombre'}
                                    </div>
                                    
                                    <div className="text-[10px] truncate opacity-90 font-medium flex items-center gap-1">
                                      <span className="w-1.5 h-1.5 rounded-full bg-current opacity-40"></span>
                                      {booking.professional_detail ? `${booking.professional_detail.first_name} ${booking.professional_detail.last_name || ''}` : 'Sin profesional'}
                                    </div>

                                    {booking.appointment_services && booking.appointment_services.length > 0 && (
                                      <div className="text-[9px] italic opacity-70 mt-auto pt-0.5 border-t border-black/5 overflow-hidden">
                                        <ul className="list-disc list-inside pl-0.5">
                                          {booking.appointment_services.slice(0, 3).map((s: any, sIdx: number) => (
                                            <li key={sIdx} className="truncate leading-3">{s.name}</li>
                                          ))}
                                          {booking.appointment_services.length > 3 && (
                                            <li className="truncate leading-3 text-gray-500">+{booking.appointment_services.length - 3} más</li>
                                          )}
                                        </ul>
                                      </div>
                                    )}
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6 relative">
            <button onClick={() => setShowCreateModal(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"><X className="w-6 h-6" /></button>
            <h3 className="text-xl font-bold text-gray-900 mb-6">Nueva Cita</h3>
            
            {showForceConfirm ? (
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
                    <div className="flex">
                        <div className="flex-shrink-0"><AlertTriangle className="h-5 w-5 text-yellow-400" /></div>
                        <div className="ml-3">
                            <p className="text-sm text-yellow-700">{conflictMessage}</p>
                            <div className="mt-4">
                                <button 
                                    type="button" 
                                    onClick={() => handleCreateSubmit(true)} 
                                    disabled={isSubmitting}
                                    className="mr-2 px-3 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {isSubmitting ? 'Agendando...' : 'Sí, agendar igual'}
                                </button>
                                <button type="button" onClick={() => setShowCreateModal(false)} className="px-3 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300">Cancelar</button>
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Profesional</label>
                        <select 
                            className="mt-1 block w-full border rounded-md p-2"
                            value={createFormData.professional}
                            onChange={e => setCreateFormData({...createFormData, professional: e.target.value})}
                        >
                            <option value="">Seleccionar...</option>
                            {professionals.map(p => (
                                <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>
                            ))}
                        </select>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Fecha</label>
                            <input type="date" className="mt-1 block w-full border rounded-md p-2" value={createFormData.date} onChange={e => setCreateFormData({...createFormData, date: e.target.value})} />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Nombre Cliente</label>
                            <input type="text" className="mt-1 block w-full border rounded-md p-2" value={createFormData.customer_name} onChange={e => setCreateFormData({...createFormData, customer_name: e.target.value})} />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Hora Inicio</label>
                            <input type="time" min="08:00" max="21:00" className="mt-1 block w-full border rounded-md p-2" value={createFormData.start_time} onChange={e => setCreateFormData({...createFormData, start_time: e.target.value})} />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Hora Fin</label>
                            <input type="time" min="08:00" max="21:00" className="mt-1 block w-full border rounded-md p-2" value={createFormData.end_time} onChange={e => setCreateFormData({...createFormData, end_time: e.target.value})} />
                        </div>
                    </div>
                    <div className="flex justify-end pt-4">
                        <button 
                            onClick={() => handleCreateSubmit(false)} 
                            disabled={isSubmitting}
                            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isSubmitting ? 'Creando...' : 'Crear Cita'}
                        </button>
                    </div>
                </div>
            )}
          </div>
        </div>
      )}

      {/* Detail Popup */}
      {showPopup && selectedBooking && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={closePopup}>
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 relative" onClick={(e) => e.stopPropagation()}>
            <button onClick={closePopup} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"><X className="w-6 h-6" /></button>
            <h3 className="text-xl font-bold text-gray-900 mb-4">Detalles de la Cita</h3>
            <div className="space-y-4">
                <p><strong>Cliente:</strong> {selectedBooking.customer_name}</p>
                <p><strong>Profesional:</strong> {selectedBooking.professional_detail?.first_name} {selectedBooking.professional_detail?.last_name}</p>
                <p><strong>Horario:</strong> {formatTime(selectedBooking.start_time)} - {formatTime(selectedBooking.end_time)}</p>
                <div>
                    <strong>Servicios:</strong>
                    {selectedBooking.appointment_services && selectedBooking.appointment_services.length > 0 ? (
                        <ul className="mt-1 list-disc list-inside text-gray-700">
                            {selectedBooking.appointment_services.map((s: any, idx: number) => (
                                <li key={idx}>{s.name}</li>
                            ))}
                        </ul>
                    ) : (
                        <span className="ml-2 text-gray-500">No especificado</span>
                    )}
                </div>
            </div>
            <div className="mt-6 flex gap-3">
              <button onClick={handleEditClick} className="flex-1 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Editar</button>
              <button onClick={handleDeleteClick} className="flex-1 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">Eliminar</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedBooking && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setShowEditModal(false)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6 relative" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setShowEditModal(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"><X className="w-6 h-6" /></button>
            <h3 className="text-xl font-bold text-gray-900 mb-6">Editar Cita</h3>
            <form onSubmit={handleEditSubmit} className="space-y-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700">Fecha</label>
                    <input type="date" value={editFormData.date} onChange={(e) => setEditFormData({ ...editFormData, date: e.target.value })} className="w-full px-4 py-2 border border-gray-300 rounded-lg" required />
                </div>
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Inicio</label>
                        <input type="time" min="08:00" max="21:00" value={editFormData.start_time} onChange={(e) => setEditFormData({ ...editFormData, start_time: e.target.value })} className="w-full px-4 py-2 border border-gray-300 rounded-lg" required />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Fin</label>
                        <input type="time" min="08:00" max="21:00" value={editFormData.end_time} onChange={(e) => setEditFormData({ ...editFormData, end_time: e.target.value })} className="w-full px-4 py-2 border border-gray-300 rounded-lg" required />
                    </div>
                </div>
                <div className="flex gap-3 pt-4">
                    <button type="button" onClick={() => setShowEditModal(false)} className="flex-1 py-2 bg-gray-100 text-gray-700 rounded-lg">Cancelar</button>
                    <button type="submit" className="flex-1 py-2 bg-blue-600 text-white rounded-lg">Guardar</button>
                </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCalendar;
